import React, { useMemo, useState } from "react";
import { Steps } from "antd";

import {
  CANONICAL_CHECKPOINT_KEYS,
  CHECKPOINT_LABELS,
  DEEMPHASIZED_CHECKPOINT_KEYS,
  type ProcessingJob,
} from "./processingTypes";
import {
  checkpointIconClass,
  checkpointIconColor,
  checkpointStatusLabel,
  checkpointStepStatus,
  deriveDisplayCheckpointStatus,
  formatDateTime,
  renderCheckpointStatusBadge,
} from "./processingDisplay";
import {
  buildCheckpointEvidence,
  checkpointSummaryLine,
} from "./checkpointEvidence";
import { livePlaybackDisplay } from "./playbackIntegrityDisplay";
import styles from "./CheckpointTimeline.module.scss";

export function CheckpointTimeline({
  job,
  uploaderLabel,
  onOpenDecisionAudit,
}: {
  job: ProcessingJob;
  uploaderLabel?: string;
  /** Opens the Decision Audit modal from metadata evidence rows. */
  onOpenDecisionAudit?: () => void;
}): React.ReactElement {
  const [openKeys, setOpenKeys] = useState<Set<string>>(() => new Set());

  const checkpointByKey = useMemo(() => {
    const map = new Map<string, (typeof job.checkpoints)[number]>();
    for (const cp of job.checkpoints ?? []) {
      map.set(cp.key, cp);
    }
    return map;
  }, [job.checkpoints]);

  const toggle = (key: string) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const items = useMemo(() => {
    return CANONICAL_CHECKPOINT_KEYS.map((key) => {
      const cp = checkpointByKey.get(key);
      const status = deriveDisplayCheckpointStatus(cp);
      const live =
        key === "ready-for-playback" ? livePlaybackDisplay(job) : null;
      const label =
        live && live.tone !== "checkpoint"
          ? live.label
          : CHECKPOINT_LABELS[key] ?? key;
      const statusLabel = checkpointStatusLabel(key, status, cp);
      const deemphasized = DEEMPHASIZED_CHECKPOINT_KEYS.has(key);
      const open = openKeys.has(key);
      const evidence = buildCheckpointEvidence(key, job, cp, {
        uploaderLabel,
        canOpenDecisionAudit: Boolean(onOpenDecisionAudit),
      });
      const summary = checkpointSummaryLine(key, job, cp);

      return {
        title: (
          <span className={deemphasized ? styles.deemphasized : undefined}>
            {label}{" "}
            {renderCheckpointStatusBadge(status, { labelOverride: statusLabel })}
          </span>
        ),
        status: checkpointStepStatus(status),
        icon: (
          <i
            className={`bi ${checkpointIconClass(status)}`}
            style={{ color: checkpointIconColor(status), fontSize: 18 }}
            aria-hidden="true"
          />
        ),
        description: (
          <div className={styles.stepBody}>
            {(cp?.startedAt || cp?.completedAt) && (
              <div className={styles.stepMeta}>
                {cp?.startedAt ? `Started ${formatDateTime(cp.startedAt)}` : ""}
                {cp?.completedAt
                  ? `${cp?.startedAt ? " · " : ""}Completed ${formatDateTime(cp.completedAt)}`
                  : ""}
              </div>
            )}
            <div className={styles.summary}>{summary}</div>
            <button
              type="button"
              className={styles.expandBtn}
              aria-expanded={open}
              onClick={() => toggle(key)}
              data-testid={`checkpoint-expand-${key}`}
            >
              <i
                className={`bi ${open ? "bi-chevron-down" : "bi-chevron-right"}`}
                aria-hidden="true"
              />
              {open ? "Hide details" : "Show details"}
            </button>
            {open ? (
              <div className={styles.evidenceList} data-testid={`checkpoint-evidence-${key}`}>
                {evidence.map((row, idx) =>
                  row.section ? (
                    <div
                      key={`${key}-section-${row.label}-${idx}`}
                      className={styles.evidenceSection}
                      role="presentation"
                    >
                      {row.label}
                    </div>
                  ) : (
                    <div key={`${key}-${row.label}-${idx}`} className={styles.evidenceRow}>
                      <span className={styles.evidenceLabel}>{row.label}</span>
                      <span className={row.muted ? styles.muted : styles.evidenceValue}>
                        {row.action === "decision-audit" && onOpenDecisionAudit ? (
                          <button
                            type="button"
                            className={styles.evidenceAction}
                            onClick={onOpenDecisionAudit}
                            data-testid="checkpoint-open-decision-audit"
                          >
                            {row.value}
                          </button>
                        ) : (
                          row.value
                        )}
                      </span>
                    </div>
                  ),
                )}
              </div>
            ) : null}
          </div>
        ),
      };
    });
  }, [checkpointByKey, job, onOpenDecisionAudit, openKeys, uploaderLabel]);

  return <Steps direction="vertical" size="small" items={items} />;
}
